import { useState, useCallback, useRef, useEffect } from 'react';
import { applyZoomDelta, getZoomLevelForScale, MIN_SCALE, MAX_SCALE } from '../utils';

/**
 * Hook for managing Gantt zoom state and smooth zoom behavior
 * 
 * APPROACH:
 * - Track pivot point as a ratio of timeline width
 * - On each frame: update scale, calculate new scroll position, apply both together
 * - Use calculated widths (not DOM reads) to avoid timing issues
 */
export const useGanttZoom = (chartRef) => {
  // React state - updates trigger column regeneration
  const [zoomScale, setZoomScale] = useState(2);
  
  // Refs for real-time values (no re-renders during animation)
  const currentScaleRef = useRef(2);
  const lastZoomLevelRef = useRef('day');
  
  // Animation state
  const animationRef = useRef(null);
  const targetScaleRef = useRef(2);
  const pivotInfoRef = useRef(null);
  
  // Derive discrete zoom level from scale (for column generation)
  const zoomLevel = getZoomLevelForScale(zoomScale);

  // Update timeline width directly via DOM (no React re-render)
  const updateTimelineWidth = useCallback((scale, totalDays) => {
    if (!chartRef.current) return;
    
    const timelineElements = chartRef.current.querySelectorAll('.gantt-timeline-header__timeline, .gantt-row__timeline, .gantt-swimlane__header-timeline');
    const newWidth = Math.max(800, totalDays * 40 * scale);
    
    timelineElements.forEach(el => {
      el.style.minWidth = `${newWidth}px`;
    });
    
    return newWidth;
  }, [chartRef]);

  // Smooth animated zoom with pivot point preservation
  const smoothZoom = useCallback((deltaY, mouseX = null) => {
    const chart = chartRef.current;
    if (!chart) return;
    
    const sidebarWidth = 200;
    const totalDays = parseFloat(chart.dataset.totalDays);
    if (!totalDays || isNaN(totalDays)) return;
    
    const currentScale = currentScaleRef.current;
    const currentTimelineWidth = Math.max(800, totalDays * 40 * currentScale);
    
    // Calculate focus point in viewport coordinates
    const chartRect = chart.getBoundingClientRect();
    const viewportWidth = chart.clientWidth;
    let focusXInViewport = mouseX !== null 
      ? mouseX - chartRect.left
      : viewportWidth / 2;
    
    // Ensure focus is in timeline area (not sidebar)
    focusXInViewport = Math.max(sidebarWidth, focusXInViewport);
    
    // Distance from sidebar edge to focus point in viewport
    const focusOffsetFromSidebar = focusXInViewport - sidebarWidth;
    
    // Absolute position in timeline (scroll + offset)
    const pivotPointPx = chart.scrollLeft + focusOffsetFromSidebar;
    
    // Convert to ratio (0-1) - this stays constant during zoom
    const pivotRatio = pivotPointPx / currentTimelineWidth;
    
    // Store pivot info at start of new zoom gesture
    if (!pivotInfoRef.current || !animationRef.current) {
      pivotInfoRef.current = {
        pivotRatio: Math.max(0, Math.min(1, pivotRatio)),
        focusOffsetFromSidebar,
        totalDays,
        sidebarWidth,
      };
    }
    
    // Calculate new target scale
    const newTargetScale = applyZoomDelta(targetScaleRef.current, deltaY);
    targetScaleRef.current = newTargetScale;
    
    // Start animation if not already running
    if (!animationRef.current) {
      const duration = 80; // ms
      let animStartTime = performance.now();
      let animStartScale = currentScale;
      let animTargetScale = newTargetScale;
      
      const animate = () => {
        const now = performance.now();
        const currentTarget = targetScaleRef.current;
        
        // If target changed mid-animation, smoothly adjust
        if (currentTarget !== animTargetScale) {
          animStartTime = now;
          animStartScale = currentScaleRef.current;
          animTargetScale = currentTarget;
        }
        
        const elapsed = now - animStartTime;
        const t = Math.min(elapsed / duration, 1);
        const progress = 1 - Math.pow(1 - t, 2); // Ease-out quadratic
        
        // Calculate interpolated scale
        const newScale = animStartScale + (animTargetScale - animStartScale) * progress;
        currentScaleRef.current = newScale;
        
        // Calculate new timeline width and update DOM
        const pivot = pivotInfoRef.current;
        const newTimelineWidth = Math.max(800, pivot.totalDays * 40 * newScale);
        updateTimelineWidth(newScale, pivot.totalDays);
        
        // Calculate where pivot point should be in new timeline
        const newPivotPointPx = pivot.pivotRatio * newTimelineWidth;
        
        // Scroll so pivot stays at same viewport position
        const newScrollLeft = newPivotPointPx - pivot.focusOffsetFromSidebar;
        
        // Apply scroll with bounds checking
        const totalWidth = newTimelineWidth + pivot.sidebarWidth;
        const maxScroll = Math.max(0, totalWidth - chart.clientWidth);
        chart.scrollLeft = Math.max(0, Math.min(maxScroll, newScrollLeft));
        
        // Update React state only when zoom level changes (for column regeneration)
        const newZoomLevel = getZoomLevelForScale(newScale);
        if (newZoomLevel !== lastZoomLevelRef.current) {
          lastZoomLevelRef.current = newZoomLevel;
          setZoomScale(newScale);
        }
        
        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          // Animation complete
          currentScaleRef.current = animTargetScale;
          targetScaleRef.current = animTargetScale;
          setZoomScale(animTargetScale);
          
          // Clear refs on next frame
          requestAnimationFrame(() => {
            animationRef.current = null;
            pivotInfoRef.current = null;
          });
        }
      };
      
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [chartRef, updateTimelineWidth]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return {
    zoomScale,
    setZoomScale: (scale) => {
      currentScaleRef.current = scale;
      targetScaleRef.current = scale;
      lastZoomLevelRef.current = getZoomLevelForScale(scale);
      setZoomScale(scale);
    },
    zoomLevel,
    isAnimating: !!animationRef.current,
    smoothZoom,
    MIN_SCALE,
    MAX_SCALE,
  };
};

export default useGanttZoom;
